import {
  isProviderDriverKind,
  MANAGED_PROVIDER_INSTANCE_ID,
  neucleosHarnessForDriverKind,
} from "@cleo/contracts";
import type { ProviderDriverKind } from "@cleo/contracts";

// The switching law both clients teach — one implementation, imported by web
// and mobile alike (M17: "the mapping answers, never a copied list"). The
// mapping authority stays `neucleosHarnessForDriverKind` in @cleo/contracts;
// this module owns the derivations and the teaching copy so the two clients
// can never drift apart sentence by sentence (DL4: one dispatcher, one
// readback).

/**
 * True when a send from these thread coordinates is governed — i.e. the
 * composer's next send will freeze a contextResolution (or refuse), never
 * return null. Client pickers key off this same predicate to disable
 * drivers with no grant harness before the server's fail-closed gate
 * refuses the turn. Web's `freezeSendResolution` asks this exact function,
 * so picker and send path cannot drift.
 */
export function isGovernedSendContext(input: {
  readonly worldKey: string | null | undefined;
  readonly sessionId: string | null | undefined;
}): boolean {
  return Boolean(input.worldKey?.trim() || input.sessionId?.trim());
}

// A governed thread records every send's frozen boundary through its driver's
// grant harness before the provider runs the turn. Drivers with no harness
// (`neucleosHarnessForDriverKind` → null) cannot record that boundary, so the
// server refuses them outright — deliberately fail-closed (O19). This is the
// picker-side statement of that same gate: a human reason to disable the
// provider instead of letting Send collect a raw ProviderValidationError.
export function governedProviderDisabledReason(input: {
  /** True when the composer's next send will carry a contextResolution. */
  governed: boolean;
  driverKind: string;
  /** Presentation label for the disabled surface: group label or instance/provider display name. */
  label: string;
}): string | null {
  if (!input.governed) {
    return null;
  }
  if (neucleosHarnessForDriverKind(input.driverKind) !== null) {
    return null;
  }
  return `${input.label} can't run governed World work. Choose a provider with a governed harness, or start an ungoverned thread.`;
}

/**
 * Can 21 (2026-10-02): a started conversation may move to another engine at
 * any message; the Send that names it carries the switch, and the engine
 * switched to is given the conversation so far. One sentence, both clients,
 * shown under another engine's rows in a started conversation.
 */
export function engineSwitchNotice(label: string): string {
  return `The conversation so far will be given to ${label}.`;
}

/** The company that receives what an engine is sent, where the driver names one. */
function vendorForDriverKind(driverKind: string | null): string | null {
  switch (driverKind === null ? null : neucleosHarnessForDriverKind(driverKind)) {
    case "claude":
      return "Anthropic";
    case "codex":
      return "OpenAI";
    default:
      return null;
  }
}

export interface EngineSwitchEnd {
  readonly instanceId: string;
  /** The driver kind behind the instance, or null when the catalog does not know it. */
  readonly driverKind: string | null;
}

/**
 * Whether a Send names another engine than the one a started conversation is
 * bound to: the one rule both clients use to say a Send is a switch (it waits
 * for the running response, never steers) and the first condition of the
 * confirmation below.
 */
export function sendSwitchesEngine(input: {
  readonly threadStarted: boolean;
  readonly boundInstanceId: string | null | undefined;
  readonly targetInstanceId: string | null | undefined;
}): boolean {
  return (
    input.threadStarted &&
    input.boundInstanceId != null &&
    input.targetInstanceId != null &&
    input.boundInstanceId !== input.targetInstanceId
  );
}

/**
 * Whether a Send that moves a started conversation from `bound` to `target`
 * asks the person first (principal, 2026-10-02): when it crosses to another
 * vendor (the harness differs), when it lands on the World-paid managed lane,
 * or when it lands on an AI SDK instance other than the bound one (an AI SDK
 * instance can reach any vendor). A move between accounts of one vendor does
 * not ask. Never a disabling rule: the person may always choose.
 */
export function engineSwitchNeedsConfirmation(input: {
  readonly threadStarted: boolean;
  readonly bound: EngineSwitchEnd | null;
  readonly target: EngineSwitchEnd;
}): boolean {
  const { bound, target } = input;
  if (
    bound === null ||
    !sendSwitchesEngine({
      threadStarted: input.threadStarted,
      boundInstanceId: bound.instanceId,
      targetInstanceId: target.instanceId,
    })
  ) {
    return false;
  }
  if (target.instanceId === MANAGED_PROVIDER_INSTANCE_ID) {
    return true;
  }
  if (target.driverKind === "aiSdk") {
    return true;
  }
  const boundHarness = bound.driverKind ? neucleosHarnessForDriverKind(bound.driverKind) : null;
  const targetHarness = target.driverKind ? neucleosHarnessForDriverKind(target.driverKind) : null;
  // An engine the mapping cannot name is treated as another vendor: asking
  // once too often is cheaper than sending the conversation unannounced.
  return boundHarness === null || targetHarness === null || boundHarness !== targetHarness;
}

/**
 * The confirmation shown before such a Send (R7 in
 * docs/internals/conversation-carry-fork-and-switch.md): what will be sent,
 * including what Cleo quoted, and on the managed lane what it costs.
 */
export function engineSwitchConfirmation(input: {
  readonly from: string;
  readonly to: string;
  readonly toDriverKind?: string | null;
  readonly managed: boolean;
}): { title: string; body: string; confirm: string; cancel: string } {
  const recipient = vendorForDriverKind(input.toDriverKind ?? null) ?? input.to;
  const charge = input.managed
    ? " This engine is paid by the World: the conversation so far is charged as input on this message and on each later one."
    : "";
  return {
    title: `Give this conversation to ${input.to}?`,
    body: `Everything said in this conversation so far, including anything Cleo quoted from files or World records, will be sent to ${recipient} to continue it.${charge}`,
    confirm: `Give it to ${input.to}`,
    cancel: `Keep ${input.from}`,
  };
}

/**
 * Correlate an open instance selection to the driver kind that owns it before
 * applying the D1 lock. Custom instance ids are routing keys, not driver
 * slugs; unknown/deleted instances stay unresolved rather than being guessed.
 */
export function resolveProviderDriverKindForLock(input: {
  providerCatalog: ReadonlyArray<{
    readonly instanceId: string;
    readonly driver: string;
  }>;
  selection: string | null;
}): ProviderDriverKind | null {
  if (input.selection === null) {
    return null;
  }
  const correlated = input.providerCatalog.find(
    (provider) => provider.instanceId === input.selection,
  );
  if (correlated && isProviderDriverKind(correlated.driver)) {
    return correlated.driver;
  }
  return null;
}

// The engine a started conversation is bound to: the session's driver first,
// then the thread's, then the selection. Used for the switch notice and the
// confirmation, never to disable another engine (Can 21, 2026-10-02; until
// then this was the D1 lock, `deriveLockedProviderKind`). Unknown driver kinds
// degrade to `null` rather than being guessed. Callers must correlate provider
// instance ids through their live catalog first.
export function deriveBoundProviderKind(input: {
  /** True once the thread has any turn, message, or session — the D1 bind point. */
  threadStarted: boolean;
  sessionProviderName: string | null;
  threadProvider: string | null;
  selectedProvider: string | null;
}): ProviderDriverKind | null {
  if (!input.threadStarted) {
    return null;
  }
  if (input.sessionProviderName && isProviderDriverKind(input.sessionProviderName)) {
    return input.sessionProviderName;
  }
  const narrowedThreadProvider =
    input.threadProvider && isProviderDriverKind(input.threadProvider)
      ? input.threadProvider
      : null;
  const narrowedSelectedProvider =
    input.selectedProvider && isProviderDriverKind(input.selectedProvider)
      ? input.selectedProvider
      : null;
  return narrowedThreadProvider ?? narrowedSelectedProvider ?? null;
}
